import { GroupBase, OptionsOrGroups } from 'react-select';

import { FOR_SALE_TRACK_SERIAL_NUMBERS } from '../constants';

export type TrackSelectOption = {
  value: string;
  label: string;
  trackName: string;
  serialNum: string;
};

const toOption = (track: {
  _id: string;
  track: string;
  serialNum: string;
}): TrackSelectOption => ({
  value: track._id,
  label: `${track.track} (${track.serialNum})`,
  trackName: track.track,
  serialNum: track.serialNum,
});

const compareSerialNumbers = (a: string, b: string) => {
  const aNumber = Number.parseInt(a, 10);
  const bNumber = Number.parseInt(b, 10);
  if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
    return aNumber - bNumber;
  }
  return a.localeCompare(b);
};

export const buildTrackSelectOptions = (
  tracks: { _id: string; track: string; serialNum: string }[],
  isGrouped: boolean,
): OptionsOrGroups<TrackSelectOption, GroupBase<TrackSelectOption>> => {
  if (!isGrouped) {
    return [...tracks]
      .sort((a, b) => compareSerialNumbers(a.serialNum, b.serialNum))
      .map((track) => toOption(track));
  }

  const forSale = FOR_SALE_TRACK_SERIAL_NUMBERS.map((serial) =>
    tracks.find((track) => track.serialNum === serial),
  )
    .filter((track): track is NonNullable<typeof track> => !!track)
    .map((track) => toOption(track));

  const internal = tracks
    .filter((track) => !FOR_SALE_TRACK_SERIAL_NUMBERS.includes(track.serialNum))
    .sort((a, b) => compareSerialNumbers(a.serialNum, b.serialNum))
    .map((track) => toOption(track));

  return [
    { label: 'For Sale', options: forSale },
    { label: 'Internal', options: internal },
  ].filter((group) => group.options.length > 0);
};

export const formatTrackOptionLabel = (option: TrackSelectOption) => (
  <span className="flex items-center">
    <span>{option.trackName}</span>
    <span className="text-slate-400 ml-1">({option.serialNum})</span>
  </span>
);

export const findTrackOption = (
  options: OptionsOrGroups<TrackSelectOption, GroupBase<TrackSelectOption>>,
  trackId: string,
): TrackSelectOption | undefined => {
  for (const entry of options) {
    if ('options' in entry) {
      const found = entry.options.find((option) => option.value === trackId);
      if (found) {
        return found;
      }
    } else if (entry.value === trackId) {
      return entry;
    }
  }
  return undefined;
};
